
import { Component, Inject, Prop, Ref, Vue } from 'vue-property-decorator';

// Components
import EstimatePositionForm from '@/components/Estimates/EstimatePositionForm.vue';
import VEstimatesPlaceholder from '@/components/Estimates/VEstimatesPlaceholder.vue';
import VEstimatesPositionsTableRow from '@/components/Estimates/VEstimatesPositionsTableRow.vue';
import VButton from '@/components/VButton/VButton.vue';
import VButtonIcon from '@/components/VButton/VButtonIcon.vue';
import VCheckbox from '@/components/VCheckbox.vue';
import VTable from '@/components/VTable.vue';
import VTableActionsRow from '@/components/VTableActionsRow.vue';
import VTableCell from '@/components/VTableCell.vue';
import VTableRow from '@/components/VTableRow.vue';

// SVG
import TrashSVG from '@/assets/trash_24px.svg';

// Utils
import { get, omit } from 'lodash';

// Api
import { updateEstimateRow } from '@/utils/api/Estimates';

// Interfaces {
import { EstimateTypeProperty } from '@/ship/Enums/EstimateDocumentTypeEnum';
import { EstimateUserTypeEnum } from '@/ship/Enums/EstimateEnums';
import EstimateStatusEnum from '@/ship/Enums/EstimateStatusEnum';
import ModalEnum from '@/ship/Enums/ModalEnum';
import { IEstimateLike, IEstimatePositionUpdateModel } from '@/ship/Models/IEstimate';
import IEstimateTableRow, { EstimateTableRow } from '@/ship/Models/IEstimateTableRow';
import { tooltipForContent } from '@/utils/Helper';
import { TranslateResult } from 'vue-i18n';

@Component({
    components: {
        VTable,
        VTableRow,
        VTableCell,
        VTableActionsRow,
        VButton,
        VCheckbox,
        VButtonIcon,

        TrashSVG,

        VEstimatesPlaceholder,
        VEstimatesPositionsTableRow,
        EstimatePositionForm,
    },
})
export default class VEstimatesPositionsTable extends Vue {
    @Ref() positionEditForm!: EstimatePositionForm;

    @Inject() readonly estimateObjectStatus!: () => EstimateStatusEnum;
    @Inject() readonly estimateObjectUserType!: () => EstimateUserTypeEnum;

    @Prop({ type: Array, default: () => [] })
    public positions!: IEstimateLike[];

    @Prop({ type: String, default: '' })
    public customerName?: string;

    @Prop({ type: String, default: '' })
    public contractorsNames?: string;

    @Prop({ type: String, default: '' })
    public objectName?: string;

    @Prop({ type: Boolean, default: false })
    public noData!: boolean;

    @Prop({ type: Boolean, default: false })
    public isAgreedByProjectManager!: boolean;

    @Prop({ type: Boolean, default: false })
    public isAgreedBySupervisor!: boolean;

    @Prop({ type: String, default: '' })
    public estimateType!: string;

    maxHeight = 43;

    leftHeaderCellsCount = 5;

    readonly childFields = ['chapters', 'subchapters', 'jobs', 'materials', 'sections'];

    readonly tooltipForContent = tooltipForContent;

    public extractedRows: IEstimateTableRow[] = [];

    get isAct() {
        return this.estimateType === EstimateTypeProperty.Act;
    }

    get hasAgreedCount() {
        const isOkEstType = this.isAct;

        const isOkForContractor = this.estimateObjectUserType() === EstimateUserTypeEnum.Contractor;
        const isOkForSupervisor = this.estimateObjectUserType() === EstimateUserTypeEnum.Supervisor;
        const isOkForProjectManager = this.estimateObjectUserType() === EstimateUserTypeEnum.ProjectManager;
        const isOkRole = isOkForContractor || isOkForSupervisor || isOkForProjectManager;

        const isOkStatus = !(
            this.estimateObjectStatus() === EstimateStatusEnum.agreedByProjectManager || this.isAgreedByProjectManager
        );

        return isOkEstType && isOkRole && isOkStatus;
    }

    get headers() {
        const headers: (string | TranslateResult)[] = [];

        headers.push(''); // exclamation
        headers.push(this.$t('page.estimates.estimate.level')); // уровень
        headers.push(this.$t('page.estimates.estimate.expense')); // статья затрат
        headers.push(this.$t('page.estimates.estimate.pp')); // п\п
        if (this.isEditable) {
            headers.push(''); // редактирование
            this.leftHeaderCellsCount += 1;
        }
        headers.push(this.$t('page.estimates.estimate.naming')); // наименование
        headers.push(this.$t('page.estimates.estimate.unitsShort')); // ед.изм
        headers.push(this.$t('page.estimates.estimate.characteristic')); // характеристика
        headers.push(this.$t('volume')); // объем
        if (this.hasAgreedCount) headers.push(this.$t('page.estimates.estimate.agreedCount')); // согл.объем
        headers.push(this.$t('cost')); // цена
        headers.push(this.$t('page.estimates.estimate.fullCost')); // общ.стоим.
        headers.push(this.$t('page.estimates.estimate.expenditure')); // расход на ед.
        headers.push(this.$t('page.estimates.estimate.note')); // примечание
        headers.push(this.$tc('remark', 0)); // замечание

        return headers;
    }

    get estimateRowTemplate() {
        let baseTemplate = '';

        if (this.isClearable) baseTemplate += '3rem '; // trash/checkbox
        baseTemplate += '3rem '; // exclamation
        baseTemplate += '10rem '; // уровень
        baseTemplate += '4.5rem '; // статья затрат
        baseTemplate += '4rem '; // п\п
        if (this.isEditable) baseTemplate += '2.5rem '; // редактирование
        baseTemplate += '19.5rem '; // наименование
        baseTemplate += '6rem '; // ед.изм
        baseTemplate += '15rem '; // характеристика
        baseTemplate += '9rem  '; // объем
        if (this.hasAgreedCount) baseTemplate += '9rem '; // согл.объем
        baseTemplate += '11rem '; // цена
        baseTemplate += '11rem '; // общ.стоим.
        baseTemplate += '6rem '; // расход на ед.
        baseTemplate += '11rem '; // примечание
        baseTemplate += '11rem'; // замечание

        return baseTemplate;
    }

    get isClearable() {
        const isClearable = this.isEstimatorEditable || this.isSupervisorEditable || this.isProjectManagerEditable;
        return isClearable;
    }

    get isEditable() {
        const isEditable = this.isClearable || this.isContractorEditable;
        return isEditable;
    }

    get isEstimatorEditable() {
        const isStatusOk =
            this.estimateStatus === EstimateStatusEnum.agreedByProjectManager ||
            (this.estimateStatus === EstimateStatusEnum.checking && this.isAgreedByProjectManager);
        const isActEditable = this.isAct && isStatusOk;
        const isAddEstEditable =
            this.estimateType === EstimateTypeProperty.AdditionalSmeta &&
            this.estimateStatus === EstimateStatusEnum.checking;

        return this.isEstimator && (isActEditable || isAddEstEditable);
    }

    get isContractorEditable() {
        const isContractorEditable =
            this.isContractor &&
            (this.estimateStatus === EstimateStatusEnum.draft || this.estimateStatus === EstimateStatusEnum.fixing);
        return isContractorEditable;
    }

    get isSupervisorEditable() {
        const isSupervisorEditable =
            this.isSupervisor && this.estimateStatus === EstimateStatusEnum.checking && this.isAct;
        return isSupervisorEditable;
    }

    get isProjectManagerEditable() {
        const isOkType = this.isAct;
        const isStatusOk =
            this.estimateStatus === EstimateStatusEnum.agreedBySupervisor ||
            (this.estimateStatus === EstimateStatusEnum.checking && this.isAgreedBySupervisor);
        const isProjectManagerEditable = isOkType && this.isProjectManager && isStatusOk;
        return isProjectManagerEditable;
    }

    get estimateStatus() {
        return this.estimateObjectStatus();
    }

    get isEstimator() {
        return this.estimateUserType === EstimateUserTypeEnum.Estimator;
    }

    get isContractor() {
        return this.estimateUserType === EstimateUserTypeEnum.Contractor;
    }

    get isProjectManager() {
        return this.estimateUserType === EstimateUserTypeEnum.ProjectManager;
    }

    get isSupervisor() {
        return this.estimateUserType === EstimateUserTypeEnum.Supervisor;
    }

    get estimateUserType() {
        return this.estimateObjectUserType();
    }

    get isAllSelected() {
        return this.extractedRows.every(({ selected }) => selected);
    }

    set isAllSelected(value: boolean) {
        this.extractedRows.forEach((row) => this.selectRow(row, value));
    }

    updateRows() {
        this.extractedRows.forEach((row) => {
            this.updateRowData(row);
        });
    }

    updateRowData(row: IEstimateTableRow) {
        let updateModel = {};

        if (this.isEstimatorEditable) {
            updateModel = { ...row, remark: '' };
        } else if (this.isSupervisorEditable || this.isProjectManagerEditable) {
            updateModel = { ...row, agreedCount: null, remark: '' };
        }

        if (row.selected) {
            this.updateRow(updateModel);
        }

        if (row.children?.length) {
            row.children.forEach((childRow) => {
                this.updateRowData(childRow);
            });
        }
    }

    async updateRow(updateModel: IEstimatePositionUpdateModel) {
        try {
            await updateEstimateRow(updateModel.id, updateModel);
            this.$emit('updated');
        } catch (error) {
            this.$modal.show(ModalEnum.Error, { error });
        }
    }

    editRow(row: EstimateTableRow) {
        this.positionEditForm.show(row);
    }

    selectRow(row: EstimateTableRow, value: boolean) {
        row.selected = value;
        if (row.children?.length) row.children.forEach((child) => this.selectRow(child, value));
    }

    public extractRowData(entity: IEstimateLike, stage: number = 1): IEstimateTableRow {
        const children: IEstimateTableRow[] = [];

        for (const key of this.childFields) {
            const childEntities = get(entity, `${key}.data`);

            if (!Array.isArray(childEntities) || !childEntities.length) continue;

            const extractedChildren = childEntities.map((child: IEstimateLike) =>
                this.extractRowData(child, stage + 1),
            );
            children.push(...extractedChildren);
        }

        return {
            ...omit(entity, this.childFields),
            isOpen: true,
            stage,
            children,
            selected: false,
        };
    }

    calcMaxHeight() {
        this.maxHeight = 43 / window.devicePixelRatio;
    }

    mounted() {
        this.extractedRows = this.positions.map((position) => this.extractRowData(position, 1));
        window.addEventListener('resize', this.calcMaxHeight);
    }

    beforeDestroy() {
        window.removeEventListener('resize', this.calcMaxHeight);
    }
}
